import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Edit2, Clock, Tag, Loader } from 'react-feather';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import api from '../services/api';

interface Document {
  id: string;
  title: string;
  url: string;
  content: string;
  created_time: string;
  last_edited_time: string;
  source: string;
  type: string;
  metadata: {
    key_points?: string[];
    document_count?: number;
    document_types?: string[];
    date_range?: {
      start: string;
      end: string;
    };
  };
}

export default function NotionDocuments() {
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch documents
  const { data: documents, isLoading, error } = useQuery({
    queryKey: ['notion-documents'],
    queryFn: async () => {
      try {
        const response = await api.get('/notion/documents');
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 401) {
          // Redirect to Notion auth if not authenticated
          const authResponse = await api.get('/notion/auth');
          if (authResponse.data.auth_url) {
            window.location.href = authResponse.data.auth_url;
          }
          throw new Error('Please authenticate with Notion first');
        }
        throw error;
      }
    },
    retry: false // Don't retry on 401
  });

  // Group documents by type
  const groupedDocuments = React.useMemo(() => {
    if (!documents) return {};
    return documents.reduce((acc, doc) => {
      const type = doc.type || 'Other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(doc);
      return acc;
    }, {} as Record<string, Document[]>);
  }, [documents]);

  const { data: pipelineStatus } = useQuery({
    queryKey: ['pipeline-status'],
    queryFn: async () => {
      const response = await fetch('https://api.usepeppr.ai/api/demo/status', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch status');
      return response.json();
    }
  });

  const renderMetadata = (doc: Document) => {
    return (
      <div className="mt-4 space-y-3 text-sm text-gray-600">
        {doc.metadata?.key_points && (
          <div>
            <h4 className="font-medium text-gray-900">Key Points:</h4>
            <ul className="mt-1 list-disc list-inside">
              {doc.metadata.key_points.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </div>
        )}
        {doc.metadata?.document_count && (
          <div className="flex items-center gap-2">
            <FileText size={14} />
            <span>{doc.metadata.document_count} source documents</span>
          </div>
        )}
        {doc.metadata?.date_range && (
          <div className="flex items-center gap-2">
            <Clock size={14} />
            <span>
              {format(new Date(doc.metadata.date_range.start), 'MMM d, yyyy')} - 
              {format(new Date(doc.metadata.date_range.end), 'MMM d, yyyy')}
            </span>
          </div>
        )}
        {doc.metadata?.document_types && (
          <div className="flex items-center gap-2">
            <Tag size={14} />
            <div className="flex flex-wrap gap-1">
              {doc.metadata.document_types.map((type, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 text-xs rounded-full bg-gray-100"
                >
                  {type}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          {error instanceof Error ? error.message : 'Failed to load documents'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Generated Documents</h1>
            <p className="mt-2 text-sm text-gray-600">
              AI-generated summaries and documentation from your integration activities
            </p>
          </div>
          <Link
            to="/dashboard"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back to Dashboard
          </Link>
        </div>

        {pipelineStatus?.is_running && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-center gap-2 text-blue-700">
              <Loader className="animate-spin" />
              <span>Processing new documents... ({pipelineStatus.progress}%)</span>
            </div>
            <p className="mt-1 text-sm text-blue-600">
              Current step: {pipelineStatus.current_step}
            </p>
          </div>
        )}

        {/* Document Categories */}
        <div className="space-y-8">
          {Object.entries(groupedDocuments).map(([type, docs]) => (
            <div key={type} className="bg-white rounded-lg shadow">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {type} ({docs.length})
                </h2>
                <div className="space-y-6">
                  {docs.map((doc) => (
                    <div 
                      key={doc.id}
                      className="border-b border-gray-200 last:border-0 pb-6 last:pb-0"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {doc.title}
                          </h3>
                          <p className="mt-2 text-gray-600">
                            {doc.content}
                          </p>
                          {renderMetadata(doc)}
                        </div>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Edit2 size={14} />
                          <span>View in Notion</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 